import {
	MouseEvent,
	ReactNode,
	isValidElement,
	useCallback,
	useEffect,
	forwardRef,
	useMemo,
	useRef,
	useState,
} from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { Button, ButtonProps, CircularProgress, alpha, useTheme } from "@mui/material";
import type { PaletteColor, SxProps, Theme } from "@mui/material/styles";

export type ProcedureButtonIntent =
	| "primary"
	| "secondary"
	| "action"
	| "edit"
	| "success"
	| "save"
	| "danger"
	| "warning"
	| "neutral"
	| "dismiss"
	| "ghost";

export type ProcedureButtonProps = ButtonProps & {
	intent?: ProcedureButtonIntent;
	semanticRole?: "save" | "delete" | "cancel" | "close" | "edit" | "actions";
	suppressSemanticIcon?: boolean;
	loading?: boolean;
	loadingText?: ReactNode;
	disableWhileLoading?: boolean;
};

const extractSemanticText = (node: ReactNode): string => {
	if (node === null || node === undefined || typeof node === "boolean") {
		return "";
	}
	if (typeof node === "string" || typeof node === "number") {
		return String(node);
	}
	if (Array.isArray(node)) {
		return node.map(extractSemanticText).join(" ");
	}
	if (isValidElement(node)) {
		const props = node.props as { children?: ReactNode; String?: string };
		return [props.String, extractSemanticText(props.children)]
			.filter(Boolean)
			.join(" ");
	}

	return "";
};

const inferSemanticRole = (
	text: string,
): ProcedureButtonProps["semanticRole"] | undefined => {
	const normalized = text.trim().toLowerCase();
	if (!normalized) return undefined;
	if (normalized.includes("delete")) return "delete";
	if (normalized.includes("save")) return "save";
	if (normalized.includes("cancel")) return "cancel";
	if (normalized.includes("close")) return "close";
	if (normalized.includes("edit")) return "edit";
	if (normalized.includes("action")) return "actions";
	return undefined;
};

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
	typeof value === "object" &&
	value !== null &&
	"then" in value &&
	typeof (value as PromiseLike<unknown>).then === "function";

const intentDefaults: Record<
	Exclude<ProcedureButtonIntent, "ghost">,
	Pick<ButtonProps, "variant" | "color">
> & {
	ghost: Pick<ButtonProps, "variant" | "color">;
} = {
	primary: { variant: "contained", color: "primary" },
	secondary: { variant: "outlined", color: "primary" },
	action: { variant: "outlined", color: "primary" },
	edit: { variant: "outlined", color: "primary" },
	success: { variant: "contained", color: "success" },
	save: { variant: "contained", color: "success" },
	danger: { variant: "contained", color: "error" },
	warning: { variant: "outlined", color: "warning" },
	neutral: { variant: "outlined", color: "inherit" },
	dismiss: { variant: "outlined", color: "inherit" },
	ghost: { variant: "text", color: "primary" },
};

type ResolvedButtonPalette = Pick<
	PaletteColor,
	"main" | "dark" | "light" | "contrastText"
>;

const getIntentPalette = (
	theme: Theme,
	color: ButtonProps["color"],
): ResolvedButtonPalette => {
	switch (color) {
		case "success":
			return theme.palette.success;
		case "error":
			return theme.palette.error;
		case "warning":
			return theme.palette.warning;
		case "info":
			return theme.palette.info;
		case "inherit":
			return {
				main: theme.palette.text.primary,
				dark: theme.palette.text.primary,
				light: theme.palette.text.secondary,
				contrastText: theme.palette.background.paper,
			};
		case "primary":
		default:
			return theme.palette.primary;
	}
};

const getContainedTextColor = (
	theme: Theme,
	color: ButtonProps["color"],
	palette: ResolvedButtonPalette,
) => {
	if (color === "warning" || color === "inherit") {
		return theme.palette.getContrastText(palette.main);
	}
	return theme.palette.common.white;
};

/**
 * Shared button for the procedures module.
 *
 * - Uses theme palette for colors
 * - Unifies border radius, height and typography
 * - Still behaves like a normal MUI Button (all props forwarded)
 *
 * You can either:
 * - Use `intent` for semantic presets (primary / danger / success / warning / ghost)
 * - Or pass `variant` / `color` directly – in that case only the shared `sx` is applied
 */
export const ProcedureButton = forwardRef<HTMLButtonElement, ProcedureButtonProps>(
	(props, ref) => {
		const {
			intent,
			sx,
			size,
			onClick,
			disabled,
			children,
			startIcon,
			endIcon,
			loading = false,
			loadingText,
			disableWhileLoading = true,
			semanticRole,
			suppressSemanticIcon = false,
			variant: explicitVariant,
			color: explicitColor,
			...rest
		} = props;
		const [internalLoading, setInternalLoading] = useState(false);
		const mountedRef = useRef(true);
		const theme = useTheme();

		useEffect(() => {
			return () => {
				mountedRef.current = false;
			};
		}, []);

		const baseHeight = size === "small" ? 32 : size === "large" ? 48 : 40;

		const resolvedDefaults = intent ? intentDefaults[intent] : {};
		const inferredSemanticRole =
			semanticRole ?? inferSemanticRole(extractSemanticText(children));
		const forceHighEmphasisVariant =
			inferredSemanticRole === "save" || inferredSemanticRole === "delete";

		const variant =
			explicitVariant && !forceHighEmphasisVariant
				? explicitVariant
				: inferredSemanticRole === "save"
					? "contained"
					: inferredSemanticRole === "delete"
						? "contained"
						: explicitVariant ?? resolvedDefaults.variant ?? "contained";
		const color =
			inferredSemanticRole === "save"
				? "success"
				: inferredSemanticRole === "delete"
					? "error"
					: explicitColor ?? resolvedDefaults.color ?? "primary";
		const isLoading = loading || internalLoading;
		const palette = getIntentPalette(theme, color);
		const containedTextColor = getContainedTextColor(theme, color, palette);
		const semanticStartIcon =
			suppressSemanticIcon || startIcon
				? startIcon
				: inferredSemanticRole === "save"
					? <SaveIcon />
					: inferredSemanticRole === "delete"
						? <DeleteIcon />
						: undefined;

		const handleClick = useCallback(
			(event: MouseEvent<HTMLButtonElement>) => {
				if (!onClick) return;
				const result = onClick(event) as unknown;
				if (isPromiseLike(result)) {
					setInternalLoading(true);
					void Promise.resolve(result)
						.catch(() => undefined)
						.finally(() => {
							if (mountedRef.current) {
								setInternalLoading(false);
							}
						});
				}
			},
			[onClick],
		);

		const spinner = useMemo(
			() => <CircularProgress color="inherit" size={16} />,
			[],
		);

		const sharedButtonSx = useMemo(
			(): SxProps<Theme> => ({
				minHeight: baseHeight,
				borderRadius: 2.5,
				textTransform: "none",
				fontWeight: 700,
				letterSpacing: 0,
				lineHeight: 1,
				px: size === "small" ? 1.5 : 2,
				boxShadow: "none",
				borderWidth: 1,
				transition:
					"background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",
				"& .MuiButton-startIcon": {
					mr: 0.75,
					"& > *:nth-of-type(1)": {
						fontSize: size === "small" ? 18 : 20,
					},
				},
				"& .MuiButton-endIcon": {
					ml: 0.75,
				},
				"&:focus-visible": {
					outline: "none",
					boxShadow: `0 0 0 3px ${alpha(palette.main, theme.palette.mode === "dark" ? 0.28 : 0.18)}`,
				},
				...(variant === "contained"
						? {
							backgroundColor: palette.main,
							color: containedTextColor,
							borderColor: alpha(palette.main, 0.92),
							boxShadow:
								theme.palette.mode === "dark"
									? `0 1px 0 ${alpha(theme.palette.common.white, 0.05)}`
									: `0 1px 2px ${alpha(theme.palette.common.black, 0.08)}`,
							"&:hover": {
								backgroundColor: palette.dark,
								borderColor: palette.dark,
								color: containedTextColor,
								boxShadow:
									theme.palette.mode === "dark"
										? `0 8px 18px ${alpha(theme.palette.common.black, 0.34)}`
										: `0 8px 20px ${alpha(palette.main, 0.18)}`,
								transform: "translateY(-1px)",
							},
							"&:active": {
								transform: "translateY(0)",
								boxShadow:
									theme.palette.mode === "dark"
										? `0 2px 6px ${alpha(theme.palette.common.black, 0.28)}`
										: `0 2px 8px ${alpha(palette.main, 0.14)}`,
							},
							"&.Mui-disabled": {
								backgroundColor: alpha(theme.palette.action.disabled, 0.18),
								color: theme.palette.action.disabled,
								borderColor: alpha(theme.palette.action.disabled, 0.24),
							},
						}
					: variant === "outlined"
						? {
								backgroundColor: theme.palette.background.paper,
								borderColor: theme.palette.divider,
								color:
									color === "inherit"
										? theme.palette.text.primary
										: palette.main,
								"&:hover": {
									backgroundColor: theme.palette.action.hover,
									borderColor:
										color === "inherit"
											? theme.palette.text.disabled
											: alpha(palette.main, 0.4),
								},
								"&.Mui-disabled": {
									borderColor: alpha(theme.palette.action.disabled, 0.22),
								},
							}
						: {
								borderColor: "transparent",
								color:
									color === "inherit"
										? theme.palette.text.primary
										: palette.main,
								"&:hover": {
									backgroundColor: alpha(
										palette.main,
										theme.palette.mode === "dark" ? 0.16 : 0.08,
									),
								},
							}),
			}),
			[baseHeight, color, containedTextColor, palette, size, theme, variant],
		);

		const composedSx: SxProps<Theme> = sx
			? [sharedButtonSx, sx]
			: sharedButtonSx;

		return (
			<Button
				ref={ref}
				{...rest}
				onClick={handleClick}
				disabled={disabled || (disableWhileLoading && isLoading)}
				variant={variant}
				color={color}
				size={size}
				startIcon={isLoading ? spinner : semanticStartIcon}
				endIcon={isLoading ? undefined : endIcon}
				aria-busy={isLoading}
				sx={composedSx}
			>
				{isLoading && loadingText ? loadingText : children}
			</Button>
		);
	},
);

ProcedureButton.displayName = "ProcedureButton";
